import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { LanguageKeys } from 'src/common/constants/languages';
import { dashboardBreadcrumbsSx } from 'src/common/components/dashboard/dashboardPageHeaderStyles';
import { getPublicPageMenuLabel } from 'src/helpers/publicPageUtils';
import usePublicPagesStore from 'src/stores/publicPagesStore';
import useSystemOptionsStore from 'src/stores/systemOptionsStore';
import { translateEmailTemplateTitle } from 'src/helpers/emailTemplateI18n';
import Icon from './icons/Icon';

interface BreadcrumbItem {
  label: string;
  path: string;
}

/**
 * Maps URL path segments to translation keys
 * Uses pageTitle wherever possible for consistency
 */
const pathToTranslationKey: Record<string, string> = {
  dashboard: 'drawer.dashboard',
  participants: 'participants.adminPageTitle',
  my: 'drawer.myParticipants',
  'my/participants': 'participants.pageTitle',
  claim: 'participants.claim.pageTitle',
  'participants/my': 'participants.pageTitle',
  editions: 'editions.pageTitle',
  users: 'drawer.users',
  competition: 'drawer.checkins',
  statistics: 'statistics.pageTitle',
  insights: 'insights.pageTitle',
  system: 'drawer.systemOptions',
  accommodations: 'system.accommodations.pageTitle',
  competitions: 'system.competitions.pageTitle',
  distances: 'system.distances.pageTitle',
  'file-manager': 'drawer.fileManager',
  pages: 'system.pages.pageTitle',
  emails: 'system.emails.pageTitle',
  'social-sharing': 'system.socialSharing.pageTitle',
  trackMaps: 'system.trackMaps.pageTitle',
  profile: 'profile.pageTitle',
  home: 'drawer.home',
  race: 'drawer.race',
  details: 'race.details.pageTitle',
  results: 'race.results.pageTitle',
  trails: 'race.trails.pageTitle',
  'useful-info': 'drawer.usefulInfo',
  accommodation: 'usefulInfo.accommodation.pageTitle',
  'count-teleki-samu': 'usefulInfo.countTelekiSamu.pageTitle',
  directions: 'usefulInfo.directions.pageTitle',
  rules: 'usefulInfo.rules.pageTitle',
  sovata: 'usefulInfo.sovata.pageTitle',
  sponsors: 'sponsors.pageTitle',
  contact: 'contact.pageTitle',
};

/**
 * Gets the translation key for a path segment
 * @param segment - The path segment
 * @param previousSegment - The previous path segment (for context)
 */
const getTranslationKey = (segment: string, previousSegment?: string): string => {
  // Handle special case: "participants" under "my" (dashboard/my/participants)
  if (segment === 'participants' && previousSegment === 'my') {
    return 'drawer.myParticipants';
  }

  if (segment === 'participants' && previousSegment === 'system') {
    return 'participants.adminPageTitle';
  }

  if (segment === 'merge' && previousSegment === 'participants') {
    return 'participants.merge.pageTitle';
  }

  if (segment === 'results' && previousSegment === 'my') {
    return 'drawer.myResults';
  }

  // Legacy: "my" after "participants"
  if (segment === 'my' && previousSegment === 'participants') {
    return 'drawer.myParticipants';
  }

  // Check for exact match first
  if (pathToTranslationKey[segment]) {
    return pathToTranslationKey[segment];
  }

  // Check for partial matches (e.g., "participants/my" -> "participants")
  const partialMatch = Object.keys(pathToTranslationKey).find(key =>
    segment.startsWith(key) || segment.includes(key)
  );

  if (partialMatch) {
    return pathToTranslationKey[partialMatch] ?? segment;
  }

  // Default: return the segment as-is (will be used as fallback)
  return segment;
};

const isMongoObjectId = (segment: string) => /^[a-f0-9]{24}$/i.test(segment);

/**
 * Dynamic Breadcrumbs component that builds breadcrumbs from the current URL
 */
const DynamicBreadcrumbs = () => {
  const location = useLocation();
  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation();
  const publicPagesById = usePublicPagesStore(s => s.publicPagesById);
  const resolveByPath = usePublicPagesStore(s => s.resolveByPath);
  const competitionsByName = useSystemOptionsStore(s => s.competitionsByName);
  const uiLang = resolvedLanguage as LanguageKeys;

  // Build breadcrumb items from the pathname
  const buildBreadcrumbs = (): BreadcrumbItem[] => {
    const pathname = location.pathname;
    const segments = pathname.split('/').filter(Boolean);

    const breadcrumbs: BreadcrumbItem[] = [];
    let currentPath = '';

    segments.forEach((segment, index) => {
      const previousSegment = index > 0 ? segments[index - 1] : undefined;
      currentPath += `/${segment}`;

      // Skip dynamic route parameters (e.g., year, competition name)
      // These are typically numeric or contain special characters
      // But allow segments that are in our translation map
      if (/^\d+$/.test(segment)) {
        // This is a numeric parameter (like year), skip it
        return;
      }

      // Route prefix only (e.g. /dashboard/my/participants); avoid a duplicate "my" crumb
      if (segment === 'my' && previousSegment === 'dashboard') {
        return;
      }

      // Skip segments that look like dynamic parameters but aren't in our map
      // (e.g., competition names with dashes that aren't "file-manager")
      if (segment.includes('-') && !pathToTranslationKey[segment] && previousSegment !== 'system') {
        // This might be a dynamic parameter, but check if it's a known route first
        // For now, we'll include it and let the translation handle it
      }

      let label: string;

      const publicPage = resolveByPath(currentPath);
      if (publicPage) {
        label = getPublicPageMenuLabel(publicPage, uiLang);
      } else if (previousSegment === 'pages') {
        if (segment === 'new') {
          label = t('system.pages.addPage');
        } else if (isMongoObjectId(segment)) {
          const page = publicPagesById?.[segment];
          label = page ? getPublicPageMenuLabel(page, uiLang) : segment;
        } else {
          label = t(getTranslationKey(segment, previousSegment), { defaultValue: segment });
        }
      } else if (previousSegment === 'emails') {
        const templateKey = decodeURIComponent(segment);
        label = translateEmailTemplateTitle(t, templateKey);
      } else if (previousSegment === 'insights' && competitionsByName?.[segment]) {
        label = competitionsByName[segment].i18n[uiLang];
      } else {
        const translationKey = getTranslationKey(segment, previousSegment);
        label = t(translationKey, { defaultValue: segment });
      }

      breadcrumbs.push({
        label,
        path: currentPath,
      });
    });

    return breadcrumbs;
  };

  const breadcrumbItems = buildBreadcrumbs();

  // Don't render if we're on the home page or only have one breadcrumb
  if (breadcrumbItems.length <= 1) {
    return null;
  }

  return (
    <Breadcrumbs
      separator={<Icon icon="caretRight" fontSize="small" />}
      aria-label="breadcrumb"
      sx={dashboardBreadcrumbsSx}
    >
      {breadcrumbItems.map((item, index) => {
        const isLast = index === breadcrumbItems.length - 1;

        if (isLast) {
          return (
            <Typography key={item.path} sx={{
              color: "text.primary"
            }}>
              {item.label}
            </Typography>
          );
        }

        return (
          <Link
            key={item.path}
            component={RouterLink}
            to={item.path}
            underline="hover"
            color="inherit"
          >
            {item.label}
          </Link>
        );
      })}
    </Breadcrumbs>
  );
};

export default DynamicBreadcrumbs;

